<!-- Create Request Wizard Modal -->
@if (showCreateWizard) {
  <div class="wizard-modal-overlay">
    <div class="wizard-modal-container">
      <app-create-request-wizard
        (close)="closeCreateWizard()"></app-create-request-wizard>
    </div>
  </div>
}

<!-- Bulk Update Modal -->
@if (showBulkUpdateModal) {
  <div>
    <app-bulk-update-modal
      [selectedRequests]="getSelectedRequestObjects()"
      (closeModal)="closeBulkUpdateModal()"></app-bulk-update-modal>
  </div>
}

<div class="requests-page">
  <!-- Page Header -->
  <div class="page-header">
    <div class="header-content">
      <h1 class="page-title">Requests</h1>
      <p class="page-subtitle">Manage and track all form print requests</p>
    </div>
    <div class="header-actions">
      <!-- Create Request Button -->
      <ukg-button
        *appHasPermission="[Permissions.AccessCreateRequestMenu]"
        emphasis="high"
        size="medium"
        parent-icon="add"
        (click)="openCreateWizard()"
        (keydown.enter)="openCreateWizard()"
        (keydown.space)="openCreateWizard(); $event.preventDefault()">
        Create Request
      </ukg-button>
      <!-- Bulk Update Button -->
      <ukg-button
        *appHasPermission="[Permissions.AccessToPrintProcessFields]"
        [style.display]="selectedCount > 0 ? '' : 'none'"
        emphasis="medium"
        size="medium"
        (click)="openBulkUpdateModal()"
        (keydown.enter)="openBulkUpdateModal()"
        (keydown.space)="openBulkUpdateModal(); $event.preventDefault()">
        Process Request(s) ({{ selectedCount }})
      </ukg-button>

      <!-- Search Input -->
      <app-search-input
        [value]="searchTerm"
        placeholder="Search requests..."
        (valueChange)="onSearchChange($any($event))"
        (clear)="clearSearch()">
      </app-search-input>
      @if (selectedCount > 0) {
        <span class="selection-count"> {{ selectedCount }} selected </span>
      }
    </div>
  </div>

  <!-- Initial Loading State -->
  @if ((loading$ | async) && !hasLoadedOnce) {
    <app-loading-state message="Loading requests..."> </app-loading-state>
  }

  <!-- Error State -->
  @if (error$ | async; as error) {
    <app-error-state
      title="Unable to Load Requests"
      [message]="error"
      (retry)="onRetry()">
    </app-error-state>
  }

  <!-- Data Grid -->
  @if (requests$ | async; as pagedResult) {
    @if ((error$ | async) === null) {
      <!-- Custom Filter Panel -->
      <app-filter-panel-wrapper (clearFilters)="clearAllFilters()">
        <!-- Status Filter -->
        <div class="filter-field">
          <label for="statusFilterSelect">Status</label>
          <select
            id="statusFilterSelect"
            class="filter-select"
            [ngModel]="statusFilter ?? ''"
            (change)="onStatusFilterChange($any($event.target).value)">
            <option value="">All Statuses</option>
            @for (status of availableStatuses; track status) {
              <option [value]="status">
                {{ status }}
              </option>
            }
          </select>
        </div>
        <!-- From Date Filter -->
        <div class="filter-field">
          <label for="fromDateFilter">From Date</label>
          <input
            id="fromDateFilter"
            type="date"
            class="filter-input"
            [value]="fromDateFilter ?? ''"
            (change)="onFromDateFilterChange($any($event.target).value)"
            aria-label="From date filter" />
        </div>
        <!-- To Date Filter -->
        <div class="filter-field">
          <label for="toDateFilter">To Date</label>
          <input
            id="toDateFilter"
            type="date"
            class="filter-input"
            [value]="toDateFilter ?? ''"
            (change)="onToDateFilterChange($any($event.target).value)"
            aria-label="To date filter" />
        </div>
      </app-filter-panel-wrapper>
      <!-- Active Filter Chips Bar -->
      <app-filter-chips-bar
        [chips]="activeColumnFilterChips"
        (clearChip)="clearColumnFilter($event)"
        (clearAll)="clearAllColumnFilters()">
      </app-filter-chips-bar>
      <!-- Grid with Data (or Empty Table) -->
      <div class="grid-container" [class.is-loading]="loading$ | async">
        <!-- Loading Overlay -->
        @if (loading$ | async) {
          <div class="grid-loading-overlay">
            <ukg-progress-circular></ukg-progress-circular>
          </div>
        }
        <!-- Results Summary -->
        <div class="grid-toolbar">
          <div class="results-info-row">
            <app-results-info
              [pageNumber]="pagedResult.pageNumber"
              [pageSize]="pagedResult.pageSize"
              [totalCount]="pagedResult.totalCount"
              itemLabel="requests">
            </app-results-info>
          </div>
        </div>
        <!-- Column Search Row -->
        <app-column-search-row
          [visibleColumns]="visibleColumns"
          [columnFilters]="columnFilters"
          [showColumnFilters]="true"
          [filterRowGridTemplate]="filterRowGridTemplate"
          [showCheckboxPlaceholder]="true"
          (columnSearch)="onColumnSearch($event.column, $event.value)"
          (clearFilter)="clearColumnFilter($event)">
        </app-column-search-row>
        <!-- Custom Slotted Table with Conditional Checkboxes -->
        <div
          class="table-scroll-container"
          (click)="handleTableClick($event)"
          (keydown)="handleTableClick($event)"
          role="grid"
          tabindex="0">
          <ukg-data-table>
            <table>
              <thead>
                <tr>
                  <th sticky="left" class="checkbox-header">
                    <ukg-checkbox
                      [checked]="selectAll && pagedResult.items.length > 0"
                      (ukgCheckboxChanged)="
                        toggleSelectAllRows(pagedResult.items)
                      ">
                    </ukg-checkbox>
                  </th>
                  @for (col of visibleColumns; track col) {
                    <th>
                      <button
                        class="sortable-header"
                        (click)="onSort(col.field)"
                        type="button"
                        [attr.aria-label]="'Sort by ' + col.header">
                        {{ col.header }}
                        <span class="sort-icon">{{
                          getSortIcon(col.field)
                        }}</span>
                      </button>
                    </th>
                  }
                </tr>
              </thead>
              <tbody>
                @for (request of pagedResult.items; track request) {
                  <tr
                    [class.ukg-table-row-selected]="isRowSelected(request.id)">
                    <td sticky="left" class="checkbox-column">
                      <ukg-checkbox
                        [checked]="isRowSelected(request.id)"
                        [disabled]="isCheckboxDisabled(request)"
                        (ukgCheckboxChanged)="onCheckboxChanged(request)">
                      </ukg-checkbox>
                    </td>
                    @for (col of visibleColumns; track col) {
                      <td [class.date-column]="col.field === 'requestDate'">
                        {{ getColumnValue(request, col.field) }}
                      </td>
                    }
                  </tr>
                }
              </tbody>
            </table>
          </ukg-data-table>
        </div>
        <!-- Pagination -->
        <app-pagination
          [pageNumber]="pagedResult.pageNumber"
          [pageSize]="pagedResult.pageSize"
          [totalPages]="pagedResult.totalPages"
          [hasPreviousPage]="pagedResult.hasPreviousPage"
          [hasNextPage]="pagedResult.hasNextPage"
          (pageChange)="onPageChange($any($event))"
          (pageSizeChange)="onPageSizeChange($any($event))">
          Page {{ pagedResult.pageNumber }} of {{ pagedResult.totalPages }}
        </app-pagination>
      </div>
    }
  }
</div>
